import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Plus, FlaskConical } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TreatmentPlanData {
  conduct?: string;
  prescriptions?: string;
  exam_requests?: string;
  instructions?: string;
  follow_up?: string;
}

interface TreatmentPlanSectionProps {
  data: TreatmentPlanData;
  onChange: (data: TreatmentPlanData) => void;
  patientId?: string | null;
}

export function TreatmentPlanSection({
  data,
  onChange,
  patientId,
}: TreatmentPlanSectionProps) {
  const navigate = useNavigate();

  const updateField = (field: keyof TreatmentPlanData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const handleCreatePrescription = () => {
    if (patientId) {
      navigate(`/app/aplicativo/prescription/novo?patientId=${patientId}`);
    } else {
      navigate("/app/aplicativo/prescription/novo");
    }
  };

  const handleRequestExams = () => {
    if (patientId) {
      // Navegar para página de exames do paciente ou criar solicitação
      navigate(`/app/pacientes/${patientId}?tab=exames`);
    } else {
      navigate("/app/pacientes");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Plano de Tratamento</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="conduct">Condutas</Label>
          <Textarea
            id="conduct"
            placeholder="Descreva as condutas adotadas no atendimento..."
            rows={4}
            value={data.conduct || ""}
            onChange={(e) => updateField("conduct", e.target.value)}
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="prescriptions">Prescrições</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCreatePrescription}
            >
              <Plus className="h-4 w-4 mr-2" />
              Criar Prescrição
            </Button>
          </div>
          <Textarea
            id="prescriptions"
            placeholder="Liste as prescrições medicamentosas ou outras prescrições..."
            rows={3}
            value={data.prescriptions || ""}
            onChange={(e) => updateField("prescriptions", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="exam_requests">Solicitações de Exames</Label>
          <Textarea
            id="exam_requests"
            placeholder="Liste os exames solicitados..."
            rows={3}
            value={data.exam_requests || ""}
            onChange={(e) => updateField("exam_requests", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="instructions">Orientações ao Paciente</Label>
          <Textarea
            id="instructions"
            placeholder="Orientações sobre tratamento, cuidados, retorno..."
            rows={3}
            value={data.instructions || ""}
            onChange={(e) => updateField("instructions", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="follow_up">Retorno</Label>
          <Textarea
            id="follow_up"
            placeholder="Quando retornar, o que observar, sinais de alerta..."
            rows={2}
            value={data.follow_up || ""}
            onChange={(e) => updateField("follow_up", e.target.value)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
